import React, {useState} from 'react';
import {
  ActionButton,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogContainer,
  Flex,
  Heading,
  Radio,
  RadioGroup,
  Text,
  TextArea
} from '@adobe/react-spectrum';
import {error} from '@react/react-spectrum/Toast';
import ThumbUp from '@spectrum-icons/workflow/ThumbUp';
import ThumbUpOutline from '@spectrum-icons/workflow/ThumbUpOutline';
import ThumbDown from '@spectrum-icons/workflow/ThumbDown';
import ThumbDownOutline from '@spectrum-icons/workflow/ThumbDownOutline';
import useRecommendationFeedback from 'state/recommendations/hooks/useRecommendationFeedback';
import useFeedbackSettings from 'state/recommendations/hooks/useFeedbackSettings';
import './recommendationFeedbackButtons.css';

const COMMENT_MAX_LENGTH = 500;

const REASONS = [
  {code: 'outdated', label: 'Outdated'},
  {code: 'incorrect_information', label: 'Incorrect information'},
  {code: 'not_applicable', label: 'Not applicable to my setup'},
  {code: 'duplicate', label: 'Duplicate of another recommendation'},
  {code: 'fix_didnt_work', label: 'Fix steps didn\'t work'},
  {code: 'other', label: 'Other'}
];

const RecommendationFeedbackButtons = ({ recommendation }) => {
  // FeedbackSettingsButton (rendered once, globally) already loads this into shared state.
  const [optedOut] = useFeedbackSettings();
  const [feedback, loading, rate] = useRecommendationFeedback(recommendation);

  // Modal dialog following a rating: the cooldown notice, or the combined
  // "want to tell us more?" reason/comment enrichment prompt.
  const [prompt, setPrompt] = useState({ type: null, message: null, rating: null });
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');
  const [enrichSubmitted, setEnrichSubmitted] = useState(false);
  const [enriching, setEnriching] = useState(false);

  const closePrompt = () => setPrompt({ type: null, message: null, rating: null });

  const handleRate = rating => {
    if (loading || feedback.my_rating === rating) return;
    rate(rating)
      .then(() => {
        setReasonCode('');
        setComment('');
        setEnrichSubmitted(false);
        setPrompt({ type: 'enrich', message: null, rating });
      })
      .catch(errorObject => {
        if (errorObject.response?.status === 429) {
          setPrompt({ type: 'cooldown', message: errorObject.response.data.error, rating: null });
        } else {
          error('An unexpected error occurred while submitting your feedback.');
        }
      });
  };

  const isDown = prompt.rating === -1;
  const canSubmitEnrichment = isDown ? (!!reasonCode || comment.trim().length > 0) : comment.trim().length > 0;

  const submitEnrichment = () => {
    if (!canSubmitEnrichment || enriching) return;
    setEnriching(true);
    rate(prompt.rating, isDown ? reasonCode : undefined, comment.trim() || undefined)
      .then(() => {
        setEnriching(false);
        setEnrichSubmitted(true);
      })
      .catch(() => {
        setEnriching(false);
        error('An unexpected error occurred while submitting your feedback.');
      });
  };

  const renderDialog = () => {
    if (prompt.type === 'cooldown') {
      return (
        <Dialog>
          <Content>
            <Text>You already rated this recommendation. {prompt.message}</Text>
          </Content>
          <ButtonGroup>
            <Button variant="cta" onPress={closePrompt}>Close</Button>
          </ButtonGroup>
        </Dialog>
      );
    }
    if (prompt.type === 'enrich') {
      if (enrichSubmitted) {
        return (
          <Dialog>
            <Heading>Thank you</Heading>
            <Content>
              <Text>Thanks for the extra detail.</Text>
            </Content>
            <ButtonGroup>
              <Button variant="cta" onPress={closePrompt}>Close</Button>
            </ButtonGroup>
          </Dialog>
        );
      }
      return (
        <Dialog>
          <Heading>{isDown ? 'Tell us more' : 'Add a comment'}</Heading>
          <Content>
            <Flex direction="column" gap="size-100">
              <Text>
                Thanks for your feedback! {isDown ? 'Want to tell us more?' : 'Want to add a comment explaining your rating?'}
              </Text>
              {isDown && (
                <RadioGroup label="What's wrong? (optional)" value={reasonCode} onChange={setReasonCode}>
                  {REASONS.map(reason => (
                    <Radio key={reason.code} value={reason.code}>{reason.label}</Radio>
                  ))}
                </RadioGroup>
              )}
              <TextArea
                aria-label="Comment"
                value={comment}
                onChange={value => setComment(value.slice(0, COMMENT_MAX_LENGTH))}
                placeholder={isDown ? 'What was wrong?' : 'What was helpful?'}
                width="100%"
              />
            </Flex>
          </Content>
          <ButtonGroup>
            <Button variant="secondary" onPress={closePrompt}>No, thanks</Button>
            <Button variant="cta" isDisabled={!canSubmitEnrichment || enriching} onPress={submitEnrichment}>
              Submit
            </Button>
          </ButtonGroup>
        </Dialog>
      );
    }
    return null;
  };

  if (optedOut) {
    return null;
  }

  return (
    <Flex direction="row" alignItems="center" gap="size-100">
      <ActionButton
        size="L"
        UNSAFE_className="recommendationFeedbackButton"
        onPress={() => handleRate(1)}
      >
        {feedback.my_rating === 1
          ? <ThumbUp UNSAFE_className="recommendationFeedbackButtonSelectedIcon" />
          : <ThumbUpOutline />}
        <span>Helpful{feedback.up > 0 ? ` ${feedback.up}` : ''}</span>
      </ActionButton>
      <ActionButton
        size="L"
        UNSAFE_className="recommendationFeedbackButton"
        onPress={() => handleRate(-1)}
      >
        {feedback.my_rating === -1
          ? <ThumbDown UNSAFE_className="recommendationFeedbackButtonSelectedIcon" />
          : <ThumbDownOutline />}
        <span>Not Helpful{feedback.down > 0 ? ` ${feedback.down}` : ''}</span>
      </ActionButton>

      <DialogContainer onDismiss={closePrompt}>
        {prompt.type && renderDialog()}
      </DialogContainer>
    </Flex>
  );
};

export default RecommendationFeedbackButtons;